import * as ImportShared from '@nx/import-shared';
import type { RootState } from '@nx/state';
import { StoreKit, createDynamicBaseQuery, prepareHeaders } from '@nx/state';
import { createApi } from '@reduxjs/toolkit/query/react';

import { APP_VERSION } from '../../constants';
import { dehydrateVisualisationState } from '../../visualisation/utils';
import type {
  CloudDataImportRequestResponse,
  ImportJobDetails,
  ImportJobWithAuraCredParams,
  ImportJobWithAuraIdParams,
  ImportJobsOverview,
  ImportJobsStatusSummary,
} from './types';
import {
  buildDynamicDataSourceParamsPayloadByDataSourceConfig,
  getImportUrls,
  parseTimeString,
  sortByTimeDesc,
} from './utils';

const CLOUD_IMPORT_JOB_TAG = 'CloudImportJob' as const;
const CLOUD_IMPORT_JOB_REDUCER_KEY = 'cloudImportJob' as const;

const getOverallStatus = (
  status: ImportShared.ApiImportJobStatus,
  result?: ImportShared.ApiImportJobResult,
): ImportJobsStatusSummary => {
  if (result?.state === 'Failure') {
    return { status: 'Error', error: result.message };
  } else if (status === '') {
    return { status: 'Submitted' };
  } else if (result?.state === 'Cancelled') {
    return { status: 'Cancelled' };
  } else if (!ImportShared.API_IMPORT_JOB_STATUS.includes(status)) {
    return { status: 'Unknown' };
  }
  return { status };
};

export const cloudImportJobsSlice = createApi({
  tagTypes: [CLOUD_IMPORT_JOB_TAG],
  reducerPath: CLOUD_IMPORT_JOB_REDUCER_KEY,
  baseQuery: createDynamicBaseQuery(
    (state: RootState) => getImportUrls(state).rootEndpoint,
    {
      prepareHeaders,
    },
    (): RootState => {
      const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
      return globalStore.getState();
    },
  ),
  endpoints: (builder) => ({
    spawnNewImportJobWithRawDataSourceParametersAndAuraCred: builder.mutation<
      CloudDataImportRequestResponse,
      ImportJobWithAuraCredParams
    >({
      query: ({ dataSourceConfig, auraParams, dataSourceId, importModelId, dataModel, visualisation }) => {
        const requestBodyWithDynamicDataSourceParams: ImportShared.CloudDataImportRequestBody = {
          dataSource: {
            ...buildDynamicDataSourceParamsPayloadByDataSourceConfig(dataSourceConfig),
            dataSourceId,
          },
          auraCredentials: auraParams,
          importConfig: {
            ignoreErrors: true,
            threads: 1,
            batchSize: 5000,
          },
          importModelMetadata: {
            version: APP_VERSION,
            importModelId,
            dataModel: dataModel,
            visualisation: dehydrateVisualisationState(visualisation),
          },
        };

        return {
          url: '/v1/jobs',
          method: 'POST',
          body: requestBodyWithDynamicDataSourceParams,
        };
      },
      invalidatesTags: [CLOUD_IMPORT_JOB_TAG],
    }),
    spawnNewImportJobWithRawDataSourceParametersAndAuraId: builder.mutation<
      CloudDataImportRequestResponse,
      ImportJobWithAuraIdParams
    >({
      query: ({ dataSourceConfig, auraParams, dataSourceId, importModelId, dataModel, visualisation }) => {
        const requestBodyWithDynamicDataSourceParams: ImportShared.CloudDataImportRequestBody = {
          dataSource: {
            ...buildDynamicDataSourceParamsPayloadByDataSourceConfig(dataSourceConfig),
            dataSourceId,
          },
          auraCredentials: auraParams,
          importConfig: {
            ignoreErrors: true,
            threads: 1,
            batchSize: 5000,
          },
          importModelMetadata: {
            version: APP_VERSION,
            importModelId,
            dataModel: dataModel,
            visualisation: dehydrateVisualisationState(visualisation),
          },
        };

        return {
          url: '/v1/jobs',
          method: 'POST',
          body: requestBodyWithDynamicDataSourceParams,
        };
      },
      invalidatesTags: [CLOUD_IMPORT_JOB_TAG],
    }),
    spawnNewImportJobWithDataSourceIdAndAuraCred: builder.mutation<
      CloudDataImportRequestResponse,
      ImportJobWithAuraCredParams
    >({
      query: ({ dataSourceId, importModelId, dataModel, visualisation, auraParams }) => {
        return {
          url: '/v2/jobs',
          method: 'POST',
          body: {
            dataSourceId,
            auraCredentials: auraParams,
            importConfig: {
              ignoreErrors: true,
              threads: 1,
              batchSize: 5000,
            },
            importModelMetadata: {
              version: APP_VERSION,
              importModelId,
              dataModel: dataModel,
              visualisation: dehydrateVisualisationState(visualisation),
            },
          },
        };
      },
      invalidatesTags: [CLOUD_IMPORT_JOB_TAG],
    }),
    spawnNewImportJobWithDataSourceIdAndAuraId: builder.mutation<
      CloudDataImportRequestResponse,
      ImportJobWithAuraIdParams
    >({
      query: ({ dataSourceId, importModelId, dataModel, visualisation, auraParams }) => {
        return {
          url: '/v2/jobs',
          method: 'POST',
          body: {
            dataSourceId,
            auraCredentials: auraParams,
            importConfig: {
              ignoreErrors: true,
              threads: 1,
              batchSize: 5000,
            },
            importModelMetadata: {
              version: APP_VERSION,
              importModelId,
              dataModel: dataModel,
              visualisation: dehydrateVisualisationState(visualisation),
            },
          },
        };
      },
      invalidatesTags: [CLOUD_IMPORT_JOB_TAG],
    }),
    getCloudImportJobs: builder.query<ImportJobsOverview[], void>({
      providesTags: (result, _, __) => {
        return result
          ? [...result.map(({ id }) => ({ type: CLOUD_IMPORT_JOB_TAG, id })), CLOUD_IMPORT_JOB_TAG]
          : [CLOUD_IMPORT_JOB_TAG];
      },
      query: () => '/v1/jobs',
      transformResponse: (data: ImportShared.ApiImportJobOverview[] | null): ImportJobsOverview[] =>
        (data ?? [])
          .sort((a, b) => sortByTimeDesc(a.submittedTime, b.submittedTime))
          .map(
            (item): ImportJobsOverview => ({
              ...item,
              submittedTimeDifference: parseTimeString(item.submittedTime),
              statusSummary: getOverallStatus(item.status, item.result),
            }),
          ),
    }),
    getCloudImportJobById: builder.query<ImportJobDetails, string>({
      providesTags: (_, __, id) => [{ type: CLOUD_IMPORT_JOB_TAG, id }],
      query: (id) => `/v1/jobs/${id}`,
      transformResponse: (data: Omit<ImportJobDetails, 'submittedTimeDifference'>): ImportJobDetails => ({
        ...data,
        submittedTimeDifference: parseTimeString(data.info.submittedTime),
      }),
    }),
    cancelCloudImportJobById: builder.mutation<string, string>({
      query: (id) => ({
        url: `/v1/jobs/${id}/cancel`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, arg) => [{ type: CLOUD_IMPORT_JOB_TAG, id: arg }],
    }),
  }),
});

export const {
  useGetCloudImportJobsQuery,
  useGetCloudImportJobByIdQuery,
  useSpawnNewImportJobWithRawDataSourceParametersAndAuraCredMutation,
  useSpawnNewImportJobWithRawDataSourceParametersAndAuraIdMutation,
  useSpawnNewImportJobWithDataSourceIdAndAuraCredMutation,
  useSpawnNewImportJobWithDataSourceIdAndAuraIdMutation,
  useCancelCloudImportJobByIdMutation,
} = cloudImportJobsSlice;
